
import { action, observable } from 'mobx';
import * as Models from 'Models/Entities';
import {
	attribute,
	entity,
	IModelAttributes,
	Model,
	ReferencePath,
} from 'Models/Model';
import UserEsignceremonywitness from 'Models/Security/Acl/UserEsignceremonywitness';
import VisitorsEsignceremonywitness from 'Models/Security/Acl/VisitorsEsignceremonywitness';
import { IAcl } from 'Models/Security/IAcl';
import moment from 'moment';
import * as AttrUtils from 'Util/AttributeUtils';
import {
	getCreatedModifiedCrudOptions,
	makeEnumFetchFunction,
	makeFetchOneToManyFunc,
} from 'Util/EntityUtils';
import * as Validators from 'Validators';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import { CRUD } from '../CRUDOptions';
import * as Enums from '../Enums';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';


export interface IEsignceremonywitnessEntityAttributes extends IModelAttributes {
	witnessstatus: Enums.esignwitnessstatus;
	signaturetimestamp: Date;
	witnessip: string;
	witnesslocation: string;
	rejectionReason?: string;
	esignceremonysigneeId: string;
	esignceremonysignee: Models.EsignceremonysigneeEntity | Models.IEsignceremonysigneeEntityAttributes;
	smsTokenId: string;
	smsToken: Models.SmsTokenEntity | Models.ISmsTokenEntityAttributes;
	esignuserId: string;
	esignuser: Models.EsignuserEntity | Models.IEsignuserEntityAttributes;

}

@entity('EsignceremonywitnessEntity', 'ESignCeremonyWitness')
export default class EsignceremonywitnessEntity extends Model
	implements IEsignceremonywitnessEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new VisitorsEsignceremonywitness(),
		new UserEsignceremonywitness(),

	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<EsignceremonywitnessEntity, Enums.esignwitnessstatus>()
	@CRUD({
		name: 'WitnessStatus',
		displayType: 'enum-combobox',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: (attr: string) => {
			return AttrUtils.standardiseEnum(attr, Enums.esignwitnessstatusOptions);
		},
		enumResolveFunction: makeEnumFetchFunction(Enums.esignwitnessstatusOptions),
		displayFunction: (attr: Enums.esignwitnessstatus) => Enums.esignwitnessstatusOptions[attr],
	})
	public witnessstatus: Enums.esignwitnessstatus;

	@observable
	@attribute<EsignceremonywitnessEntity, Date>()
	@CRUD({
		name: 'SignatureTimeStamp',
		displayType: 'datetimepicker',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseDate,
	})
	public signaturetimestamp: Date;

	@observable
	@attribute<EsignceremonywitnessEntity, string>()
	@CRUD({
		name: 'WitnessIP',
		displayType: 'textfield',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public witnessip: string;

	@observable
	@attribute<EsignceremonywitnessEntity, string>()
	@CRUD({
		name: 'WitnessLocation',
		displayType: 'textfield',
		order: 40,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public witnesslocation: string;
	
	@observable
	@attribute<EsignceremonywitnessEntity, string>()
	@CRUD({
		name: 'RejectionReason',
		displayType: 'textfield',
		order: 45,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public rejectionReason?: string;

	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		
		name: 'ESignCeremonySignee',
		displayType: 'reference-combobox',
		order: 50,
		referenceTypeFunc: () => Models.EsignceremonysigneeEntity,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'esignceremonysignees',
			oppositeEntity: () => Models.EsignceremonysigneeEntity,
		}),
		
	})
	public esignceremonysigneeId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public esignceremonysignee: Models.EsignceremonysigneeEntity;

	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		
		name: 'SMS Token',
		displayType: 'reference-combobox',
		order: 60,
		referenceTypeFunc: () => Models.SmsTokenEntity,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'smsTokens',
			oppositeEntity: () => Models.SmsTokenEntity,
		}),
		
	})
	public smsTokenId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public smsToken: Models.SmsTokenEntity;

	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		
		name: 'ESignUser',
		displayType: 'reference-combobox',
		order: 70,
		referenceTypeFunc: () => Models.EsignuserEntity,
		
	})
	public esignuserId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public esignuser: Models.EsignuserEntity;



	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IEsignceremonywitnessEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IEsignceremonywitnessEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.witnessstatus !== undefined) {
				this.witnessstatus = attributes.witnessstatus;
			}
			if (attributes.signaturetimestamp !== undefined) {
				if (attributes.signaturetimestamp === null) {
					this.signaturetimestamp = attributes.signaturetimestamp;
				} else {
					this.signaturetimestamp = moment(attributes.signaturetimestamp).toDate();
				}
			}
			if (attributes.witnessip !== undefined) {
				this.witnessip = attributes.witnessip;
			}
			if (attributes.witnesslocation !== undefined) {
				this.witnesslocation = attributes.witnesslocation;
			}
			if (attributes.esignceremonysigneeId !== undefined) {
				this.esignceremonysigneeId = attributes.esignceremonysigneeId;
			}
			if (attributes.esignceremonysignee !== undefined) {
				if (attributes.esignceremonysignee === null) {
					this.esignceremonysignee = attributes.esignceremonysignee;
				} else if (attributes.esignceremonysignee instanceof Models.EsignceremonysigneeEntity) {
					this.esignceremonysignee = attributes.esignceremonysignee;
					this.esignceremonysigneeId = attributes.esignceremonysignee.id;
				} else {
					this.esignceremonysignee = new Models.EsignceremonysigneeEntity(attributes.esignceremonysignee);
					this.esignceremonysigneeId = this.esignceremonysignee.id;
				}
			}
			if (attributes.smsTokenId !== undefined) {
				this.smsTokenId = attributes.smsTokenId;
			}
			if (attributes.smsToken !== undefined) {
				if (attributes.smsToken === null) {
					this.smsToken = attributes.smsToken;
				} else if (attributes.smsToken instanceof Models.SmsTokenEntity) {
					this.smsToken = attributes.smsToken;
					this.smsTokenId = attributes.smsToken.id;
				} else {
					this.smsToken = new Models.SmsTokenEntity(attributes.smsToken);
					this.smsTokenId = this.smsToken.id;
				}
			}
			if (attributes.esignuserId !== undefined) {
				this.esignuserId = attributes.esignuserId;
			}
			if (attributes.esignuser !== undefined) {
				if (attributes.esignuser === null) {
					this.esignuser = attributes.esignuser;
				} else if (attributes.esignuser instanceof Models.EsignuserEntity) {
					this.esignuser = attributes.esignuser;
					this.esignuserId = attributes.esignuser.id;
				} else {
					this.esignuser = new Models.EsignuserEntity(attributes.esignuser);
					this.esignuserId = this.esignuser.id;
				}
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
		esignceremonysignee {
			${Models.EsignceremonysigneeEntity.getAttributes().join('\n')}
		}
		smsToken {
			${Models.SmsTokenEntity.getAttributes().join('\n')}
		}
		esignuser {
			${Models.EsignuserEntity.getAttributes().join('\n')}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
							'esignceremonysignee',
							'smsToken',
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(EsignceremonywitnessEntity.prototype, 'created');
CRUD(modifiedAttr)(EsignceremonywitnessEntity.prototype, 'modified');
